'use client'

import { getAccountingOnboardingStatus } from '@/lib/api/financial/financial-accounting-api'
import { ReactNode, useEffect, useState } from 'react'
import { Navigate } from 'react-router-dom'

interface Props {
    children: ReactNode
}

type State =
    | { status: 'loading' }
    | { status: 'ok' }
    | { status: 'incomplete' }
    | { status: 'error' }

/**
 * Wrapper de rota: bloqueia o acesso a paginas contabeis (DRE, Balancete,
 * Razao, Fluxo de Caixa, Balanco Patrimonial, Journal Entries, Regras) quando
 * a empresa ainda nao concluiu o onboarding. Redireciona para o dashboard,
 * onde o wizard abre automaticamente.
 *
 * Use apenas em rotas que dependem das contas/regras/conta-caixa configuradas.
 * NAO embrulhe o proprio dashboard — ele tem o wizard embutido.
 */
export default function RequireAccountingOnboarding({ children }: Props) {
    const [state, setState] = useState<State>({ status: 'loading' })

    useEffect(() => {
        getAccountingOnboardingStatus()
            .then((res) => {
                if (res.success && res.result) {
                    setState({
                        status: res.result.completed ? 'ok' : 'incomplete',
                    })
                } else {
                    setState({ status: 'error' })
                }
            })
            .catch(() => setState({ status: 'error' }))
    }, [])

    if (state.status === 'loading') {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <span className="loading loading-spinner loading-lg text-secondary" />
            </div>
        )
    }

    if (state.status === 'incomplete') {
        // Redireciona para o dashboard, onde o wizard de configuracao abre.
        return <Navigate to="/financial/accounting" replace />
    }

    // Em caso de erro de rede/permissao, deixa entrar — a propria pagina ja
    // tratara o estado vazio.
    return <>{children}</>
}
